/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Grid, IconButton, Modal } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import CloseIcon from '@material-ui/icons/Close';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import * as S from './styles';
import { Title } from 'components/Texts';
import PhotoRank from 'components/widgets/WidgetAction/Results/PhotoResult/PhotoRank';
import { useImgSrc } from 'core/utils/use-img-src.hook';
import { ChallengeResultType } from 'interfaces';
import { userInfosSelector } from 'reducers/app.reducer';
import { challengeSelector } from 'reducers/challenge.reducer';
import Voting from '../Voting';

const { cssBorderRadius } = S;

const PhotoModal = ({
  open,
  result,
  onClose,
  onClickPrevious,
  onClickNext,
  withVotingButton,
}: {
  open: boolean;
  result?: ChallengeResultType;
  onClose: () => void;
  onClickPrevious: () => void;
  onClickNext: () => void;
  withVotingButton: boolean;
}): JSX.Element => {
  const { t } = useTranslation();
  const { loading, src } = useImgSrc(result?.image?.id);

  const { id: challengeId }: { id: string } = useParams();
  const { data: challengeData } = useSelector(
    challengeSelector(Number(challengeId)),
  );
  const areResultsAnonymized = challengeData.areResultsAnonymized;

  const { id: userId } = useSelector(userInfosSelector);
  const isMe = result?.entity['@id'] === `/v2/users/${userId}`;
  const hasMyVote =
    result?.voters
      ?.map((voter) => voter['@id'])
      .includes(`/v2/users/${userId}`) || false;

  return (
    <Modal open={open} onClose={onClose} css={{ marginTop: '10vh' }}>
      <S.ModalCard>
        <S.CloseButtonContainer>
          <S.CloseButton onClick={onClose}>
            <CloseIcon fontSize="large" />
          </S.CloseButton>
        </S.CloseButtonContainer>
        {areResultsAnonymized && !isMe ? (
          <Title variant="h4" weight="normal" isParagraph>
            {t('challenges.participant')}
          </Title>
        ) : (
          <Title variant="h4" weight="normal" isParagraph>
            {result?.entity?.profile?.firstName}{' '}
            <strong>{result?.entity?.profile?.lastName}</strong>
          </Title>
        )}
        <S.ImgContainer>
          {loading ? (
            <Skeleton
              height={300}
              width="100%"
              variant="rect"
              css={cssBorderRadius}
            />
          ) : (
            <S.Img src={src} alt={`${result?.image?.id}`} />
          )}
        </S.ImgContainer>
        <S.RankContainer>
          <PhotoRank
            challengeResults={result}
            size="normal"
            isHighlighted={isMe}
          />
        </S.RankContainer>
        <Grid container>
          <Grid item>
            <IconButton onClick={onClickPrevious}>
              <ArrowBackIosIcon />
            </IconButton>
          </Grid>
          <Grid item xs>
            {result && (
              <Voting
                result={result}
                isMe={isMe}
                hasMyVote={hasMyVote}
                withVotingButton={withVotingButton}
              />
            )}
          </Grid>
          <Grid item>
            <IconButton onClick={onClickNext}>
              <ArrowForwardIosIcon />
            </IconButton>
          </Grid>
        </Grid>
      </S.ModalCard>
    </Modal>
  );
};

export default PhotoModal;
